import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { GenericDialog } from 'app/components/Alerts/GenericDialog';
import { CueNotesContainer } from 'app/components/Tasks/CueNotes/CueNotesContainer';
import { TaskSidePanel } from 'app/components/Tasks/CustomComponents/TaskSidePanel/TaskSidePanel';
import { taskDetailsActions } from 'app/components/Tasks/store/Tasks.redux';
import {
  selectCueNotesList,
  selectCueRecipient,
  selectCurrentUserCuedTo,
  selectSidePanelChoosen,
  SelectedContainer,
  selectTaskStickChosen,
} from 'app/components/Tasks/store/Tasks.selectors';
import { TaskSidebarContainer } from 'app/components/Tasks/TaskSidebar/TaskSidebarContainer';
import { ITaskStickChosen, TaskEntity } from 'app/models/Tasks/Tasks';
import { selectUser } from 'oidc/user.selectors';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSessionValue,
  removeSessionValue,
  SessionKey,
  setSessionValue,
  StorageType,
} from 'utils/customHooks/sessionStorage/sessionHelpers';
import { PlacementDetailsStatus } from './PlacementDetailsStatus';
import { IPlacementDetails } from 'app/models/Placement/PlacementDetails';
import { selectPlacementDetails, selectPlacementSsnErrorDialog } from 'store/redux-store/placement-details/selectors';
import { placementDetailsAction } from 'store/redux-store/placement-details/slice';
import { SsnErrorDialog } from '../../NextPlacementStatus/SsnErrorDialog';
import { selectDrawerData } from 'store/redux-store/global-search/selectors';
import ComplianceStatusProgressNotesIcon from '../Edit/Components/CSPNotes/ComplianceStatusProgressNotesIcon';

const useStyles = makeStyles<{ taskShrink: boolean }>()((theme, props) => ({
  sidePanel: {
    width: props.taskShrink ? 72 : '297px',
    '& .MuiPaper-root.MuiPaper-elevation1': {
      top: '60px',
      height: '93%',
      zIndex: 99,
    },
    '& .MuiPaper-root': {
      zIndex: 100,
    },
  },
  statusBtnContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'center',
    alignItems: 'center',
  },
  iconContainer: {
    paddingLeft: '8px !important'
  },
  placementDetailsStatusContainer: {
    paddingRight: '24px'
  },
  cspNotesIconContainer: {
    paddingTop: '6px'
  },
}));

export const PlacementDetailsStatusWrapper = ({
  stickIconArray,
  taskStickChosen,
}: {
  stickIconArray: number[];
  taskStickChosen?: ITaskStickChosen;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const panelChoosen = useSelector(selectSidePanelChoosen);
  const userInfo = useSelector(selectUser);
  const recipientList = useSelector(selectCueRecipient);
  const cueNotesList = useSelector(selectCueNotesList);
  const cueToUser = useSelector(selectCurrentUserCuedTo);
  const container = useSelector(SelectedContainer);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [stickValue, setStickValue] = useState<number | null>(null);
  const [changePanel, setChangePanel] = useState<boolean>(true);
  const [clearCueField, setClearCueField] = useState<boolean>(false);
  const [lastRecipientId, setLastRecipientId] = useState<number | null>();
  const [isUserCuedTo, setUserCuedTo] = useState<boolean>(false);
  const [tooltipDisplay, setToolTipDisplay] = useState<any>(t('notification.taskSidePanel.cueSidePanelHeading'));
  const foundPlacement: IPlacementDetails = useSelector(selectPlacementDetails);
  const selectedTaskStick = useSelector(selectTaskStickChosen);
  const drawerData = useSelector(selectDrawerData);
  const ssnErrorDialog = useSelector(selectPlacementSsnErrorDialog);

  useEffect(() => {
    if (cueNotesList?.length > 0) {
      const lastRecipientData = cueNotesList[0]?.lastRecipientId;
      setLastRecipientId(lastRecipientData);
      if (userInfo?.userInfo?.employeeId === lastRecipientData) {
        setUserCuedTo(true);
        dispatch(taskDetailsActions.setCurrentUserCuedTo(true));
      } else {
        dispatch(taskDetailsActions.setCurrentUserCuedTo(false));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cueNotesList]);

  useEffect(() => {
    if (cueNotesList?.length === 0) {
      setToolTipDisplay(t('notification.taskSidePanel.cueSidePanelHeading'));
      dispatch(taskDetailsActions.setUserCuedTo(false));
    } else {
      if (isUserCuedTo) {
        const lastRecipient = recipientList?.find(
          recipient => recipient?.employeeId === userInfo?.userInfo?.employeeId,
        );
        const name = lastRecipient?.firstName + ' ' + lastRecipient?.lastName;
        setToolTipDisplay(t('notification.taskSidePanel.cueNotesHeading') + name);
        dispatch(taskDetailsActions.setUserCuedTo(true));
      } else if (!isUserCuedTo && lastRecipientId && lastRecipientId > 0) {
        const lastRecipient = recipientList?.filter(recipient => recipient?.employeeId === lastRecipientId)[0];
        const name = lastRecipient?.firstName + ' ' + lastRecipient?.lastName;
        dispatch(taskDetailsActions.setUserCuedTo(true));
        setToolTipDisplay(t('notification.taskSidePanel.cueNotesHeading') + name);
      } else if (lastRecipientId === null) {
        dispatch(taskDetailsActions.setUserCuedTo(false));
        setToolTipDisplay(
          t('notification.taskSidePanel.cueNotesHeading') + t('notification.taskSidePanel.cueNotes.noCue'),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastRecipientId, cueToUser]);

  const taskSidePanelItems = [
    {
      title: t('notification.taskSidePanel.taskSidePanelHeading'),
      tooltip: t('notification.taskSidePanel.taskSidePanelHeading'),
      component: <TaskSidebarContainer container={TaskEntity.PLACEMENTS} />,
    },
    {
      title: t('notification.taskSidePanel.cueSidePanelHeading'),
      tooltip: tooltipDisplay,
      component: (
        <CueNotesContainer
          setChangePanel={setChangePanel}
          clearCueField={clearCueField}
          container={TaskEntity.PLACEMENTS}
        />
      ),
    },
    {
      title: t('notification.taskSidePanel.close'),
      tooltip: t('notification.taskSidePanel.close'),
      component: <></>,
    },
  ];

  let sidePanelItems = taskSidePanelItems;
  const { classes } = useStyles({ taskShrink: taskStickChosen.stick === null });

  const validateTabSwitch = () => {
    setOpenDialog(changePanel == false);
  };

  const handleTaskStickChoice = stick => {
    validateTabSwitch();
    if (stick === 2 && container === 1) {
      if (changePanel) {
        dispatch(
          taskDetailsActions.setTaskStickChosen({
            entity: TaskEntity.PLACEMENTS,
            stick: null,
          }),
        );
        removeSessionValue(SessionKey.taskSidePanel, StorageType.sessionStorage);
      }
      setStickValue(null);
    } else {
      dispatch(taskDetailsActions.setCueNoteReplyParent(undefined));
      changePanel &&
        dispatch(
          taskDetailsActions.setTaskStickChosen({
            entity: TaskEntity.PLACEMENTS,
            stick: stick,
          }),
        );
      changePanel && setClearCueField(false);
      changePanel && (sidePanelItems = taskSidePanelItems);
      setStickValue(stick);
    }
  };

  const handlePlacementLoad = () => {
    setClearCueField(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setClearCueField(false);
  };
  const handleDialogYes = () => {
    setChangePanel(true);
    setClearCueField(true);
    setOpenDialog(false);
  };

  useEffect(() => {
    if (changePanel && !panelChoosen) {
      if (stickValue !== taskStickChosen.stick) {
        handleTaskStickChoice(stickValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changePanel]);

  useEffect(() => {
    const getTaskPanel = getSessionValue(SessionKey.taskSidePanel, StorageType.sessionStorage);
    const selectedStick = selectedTaskStick?.stick ?? getTaskPanel?.stickValue ?? null;
    dispatch(
      taskDetailsActions.setTaskStickChosen({
        entity: TaskEntity.PLACEMENTS,
        stick: selectedStick,
      }),
    );
    setStickValue(selectedStick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSessionValue(
      SessionKey.taskSidePanel,
      {
        stickValue: taskStickChosen.stick,
        container: container,
      },
      StorageType.sessionStorage,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskStickChosen]);

  useEffect(() => {
    handlePlacementLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerData?.placementId, foundPlacement?.header?.placementId]);

  const sidePanel = (
    <>
      {stickIconArray && sidePanelItems && (
        <TaskSidePanel
          items={sidePanelItems}
          stickChosen={taskStickChosen}
          handleChoice={handleTaskStickChoice}
          iconArray={stickIconArray}
          scrollable={true}
          changePanel={changePanel}
        />
      )}
    </>
  );

  return (
    <React.Fragment>
      <SsnErrorDialog
        open={ssnErrorDialog?.open ?? false}
        title={ssnErrorDialog?.title ?? ''}
        message={ssnErrorDialog?.message ?? ''}
        onClose={() => dispatch(placementDetailsAction.closeSsnErrorDialog())}
      />
      <GenericDialog
        open={openDialog}
        fullWidth
        dialogTitleProps={{
          text: 'Add Cue Note',
        }}
        dialogContentProps={{ variant: 'text' }}
        dialogActions={[
          {
            text: t('notification.taskSidePanel.cueNotes.alertBtnCancel'),
            color: 'tertiary',
            onClick: handleDialogClose,
          },
          {
            text: t('notification.taskSidePanel.cueNotes.alertBtnYes'),
            variant: 'contained',
            color: 'primary',
            onClick: handleDialogYes,
          },
        ]}
      >
        {t('notification.taskSidePanel.cueNotes.alertContent')}
      </GenericDialog>
      <Grid container spacing={2} wrap="nowrap" sx={{ alignItems: 'center' }}>
        <Grid item classes={{ root: classes.statusBtnContainer }}>
          <Grid item className={classes.placementDetailsStatusContainer}>
            <PlacementDetailsStatus />
          </Grid>
          <Grid onClick={() => handleTaskStickChoice(2)} item className={classes.cspNotesIconContainer}>
            <ComplianceStatusProgressNotesIcon />
          </Grid>
        </Grid>
        <Grid item className={classes.iconContainer}>
          <div className={classes.sidePanel}>{sidePanel}</div>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};
